/**
 * useDeploymentWatcher — polls `/api/version` periodically and force-reloads
 * the SPA the moment the backend reports a new build hash. This unblocks
 * users who would otherwise stick on an old HTML/JS shell after a redeploy
 * because their browser/CDN cached the previous bundle.
 *
 * Strategy:
 * 1. On mount, fetch the current build hash and remember it.
 * 2. Every POLL_MS, fetch again. If the hash differs, hard-reload the page
 *    with a cache-busting query parameter so the freshest assets load.
 * 3. We also listen to `visibilitychange` so the comparison runs as soon as
 *    a tab regains focus (mobile users coming back to the site).
 */
import { useEffect, useRef } from "react";

const POLL_MS = 60 * 1000; // 1 minute

export const useDeploymentWatcher = () => {
  const knownVersionRef = useRef(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    let cancelled = false;
    let timer = null;

    const fetchVersion = async () => {
      try {
        const r = await fetch(`${base}/api/version?cb=${Date.now()}`, {
          cache: "no-store",
        });
        if (!r.ok) return null;
        const data = await r.json();
        // Combine the git short hash + boot timestamp so we trigger a
        // reload either when a fresh deploy lands (hash changes) or when
        // the backend restarts with new code (built_at changes).
        const v = data?.version || data?.build_hash || "";
        const b = data?.built_at || "";
        return v || b ? `${v}::${b}` : null;
      } catch {
        return null;
      }
    };

    const tick = async () => {
      const v = await fetchVersion();
      if (cancelled || !v) return;
      if (knownVersionRef.current === null) {
        knownVersionRef.current = v;
        return;
      }
      if (v !== knownVersionRef.current) {
        // New deploy detected — hard-reload to pick up the new shell.
        const u = new URL(window.location.href);
        u.searchParams.set("_v", v.slice(0, 8));
        window.location.replace(u.toString());
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };

    tick();
    timer = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
};
